/**
 * useSpinePreview
 *
 * React hook that bridges spine component data with the SpineOffscreenRenderer
 * to produce preview images (blob URLs) for the scene view.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { SpineAsset } from '../../core/types/spine.types';
import { spineOffscreenRenderer } from '../services/SpineOffscreenRenderer';

interface UseSpinePreviewOptions {
  spineAsset: SpineAsset | null;
  skinName: string;
  animationName: string | null;
  isPlaying: boolean;
  loop: boolean;
  timeScale: number;
  mixDuration: number;
}

interface UseSpinePreviewResult {
  previewUrl: string | null;
  loading: boolean;
  error: string | null;
  width: number;
  height: number;
}

export function useSpinePreview(options: UseSpinePreviewOptions): UseSpinePreviewResult {
  const { spineAsset, skinName, animationName, isPlaying, loop, timeScale, mixDuration } = options;

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const sessionIdRef = useRef<string | null>(null);
  const snapshotUrlRef = useRef<string | null>(null);
  const lastAnimFrameUrlRef = useRef<string | null>(null);

  // Cleanup helper
  const stopCurrentAnimation = useCallback(() => {
    if (sessionIdRef.current) {
      spineOffscreenRenderer.stopAnimation(sessionIdRef.current);
      sessionIdRef.current = null;
    }
  }, []);

  const revokeSnapshotUrl = useCallback(() => {
    if (snapshotUrlRef.current) {
      URL.revokeObjectURL(snapshotUrlRef.current);
      snapshotUrlRef.current = null;
    }
  }, []);

  // Render a static snapshot (when not playing)
  useEffect(() => {
    if (!spineAsset || isPlaying) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const renderSnapshot = async () => {
      try {
        const result = await spineOffscreenRenderer.renderSnapshot(
          spineAsset.skeletonPath,
          spineAsset.atlasPath,
          spineAsset.texturePaths,
          {
            skinName: skinName || undefined,
            animationName: animationName || undefined,
          }
        );

        if (!cancelled) {
          // Revoke old URL only AFTER new one is ready — prevents white flash
          const oldUrl = snapshotUrlRef.current;
          snapshotUrlRef.current = result.url;
          setPreviewUrl(result.url);
          setDimensions({ width: result.width, height: result.height });
          setLoading(false);
          if (oldUrl) URL.revokeObjectURL(oldUrl);
        } else {
          URL.revokeObjectURL(result.url);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      }
    };

    renderSnapshot();

    return () => {
      cancelled = true;
    };
  }, [spineAsset?.id, spineAsset?.skeletonPath, skinName, animationName, isPlaying, revokeSnapshotUrl]);

  // Start/stop animation
  useEffect(() => {
    console.log('[SpinePreview] anim effect:', { isPlaying, animationName, hasAsset: !!spineAsset });
    if (!spineAsset || !isPlaying || !animationName) {
      stopCurrentAnimation();
      return;
    }

    // Stop existing session before starting new one
    console.log('[SpinePreview] → calling startAnimation:', animationName);
    stopCurrentAnimation();
    const sessionId = spineOffscreenRenderer.startAnimation(
      spineAsset.skeletonPath,
      spineAsset.atlasPath,
      spineAsset.texturePaths,
      animationName,
      loop,
      (frameUrl, frameWidth, frameHeight) => {
        // Guard: ignore frames from stale/orphaned sessions
        if (sessionIdRef.current !== sessionId) {
          URL.revokeObjectURL(frameUrl);
          return;
        }
        // Revoke previous animation frame URL
        if (lastAnimFrameUrlRef.current) {
          URL.revokeObjectURL(lastAnimFrameUrlRef.current);
        }
        lastAnimFrameUrlRef.current = frameUrl;
        setPreviewUrl(frameUrl);
        if (frameWidth > 0 && frameHeight > 0) {
          setDimensions(prev =>
            prev.width === frameWidth && prev.height === frameHeight
              ? prev : { width: frameWidth, height: frameHeight }
          );
        }
      },
      {
        skinName: skinName || undefined,
        timeScale,
        mixDuration,
      }
    );

    sessionIdRef.current = sessionId;

    return () => {
      stopCurrentAnimation();
    };
  }, [spineAsset?.id, spineAsset?.skeletonPath, animationName, isPlaying, loop, skinName, stopCurrentAnimation]);

  // Live update: timeScale
  useEffect(() => {
    if (sessionIdRef.current) {
      spineOffscreenRenderer.setTimeScale(sessionIdRef.current, timeScale);
    }
  }, [timeScale]);

  // Live update: mixDuration
  useEffect(() => {
    if (sessionIdRef.current) {
      spineOffscreenRenderer.setMixDuration(sessionIdRef.current, mixDuration);
    }
  }, [mixDuration]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCurrentAnimation();
      revokeSnapshotUrl();
      if (lastAnimFrameUrlRef.current) {
        URL.revokeObjectURL(lastAnimFrameUrlRef.current);
        lastAnimFrameUrlRef.current = null;
      }
    };
  }, [stopCurrentAnimation, revokeSnapshotUrl]);

  return {
    previewUrl,
    loading,
    error,
    width: dimensions.width,
    height: dimensions.height,
  };
}
